import baseApi from "../../utils/baseApi";

const SUPERADMIN_FOLDERS_API = "/superadmin/space-folders";
const SUPERADMIN_FILES_API = "/superadmin/space-file";

class SuperAdminFolderService {
    static async getSpaceContent (spaceId:string) {
        try {
            const response = await baseApi.get(`${SUPERADMIN_FOLDERS_API}/contents?spaceId=${spaceId}`);
            return response.data;
        } catch (error) {
            throw new Error("Failed to get folder.");
        }
    }

    static async getSpaceContentByCompany (companyId:string) {
        try {
            const response = await baseApi.get(`${SUPERADMIN_FOLDERS_API}/contentsByCompany?companyId=${companyId}`);
            return response.data;
        } catch (error) {
            throw new Error("Failed to get folder.");
        }
    }

    static async getSpaceContentByUserId (userId:string) {
        try {
            const response = await baseApi.get(`${SUPERADMIN_FOLDERS_API}/contents/user/?userId=${userId}`);
            return response.data;
        } catch (error) {
            throw new Error("Failed to get folder.");
        }
    }

    static async addFolder (data: { folder_name: string; spaceId: string }) {
        try {
            await baseApi.post(SUPERADMIN_FOLDERS_API, data);
        } catch (error) {
            throw new Error("Failed to add folder.");
        }
    };

    static async getFolderDetail (folderId: string, spaceId: string) {
        try {
            const response = await baseApi.get(`${SUPERADMIN_FOLDERS_API}?spaceId=${spaceId}&spaceFolderId=${folderId}`);
            return response.data; 
        } catch (error) {
            throw new Error("Failed to fetch folder details.");
        }
    };

    static async updateFolder (data: { space_folder_id: string, folder_name: string, spaceId: string })  {
        try {
            await baseApi.put(`${SUPERADMIN_FOLDERS_API}`, data);
        } catch (error) {
            throw new Error("Failed to update folder.");
        }
    };

    static async removeFolder (spaceId: string, folderId: number) {
        console.log('REMOVE ADMIN');
        try {
            await baseApi.delete(`${SUPERADMIN_FOLDERS_API}?spaceId=${spaceId}&spaceFolderId=${folderId}`);
        } catch (error) {
            throw new Error("Failed to delete folder.");
        }
    };

    // sub folder 
    static async addSubFolder (data: { folder_name: string, parent_folder_id: number, spaceId: number }) {
        try {
            await baseApi.post(`${SUPERADMIN_FOLDERS_API}`, data);
        } catch (error) {
            throw new Error("Failed to add user subfolder.");
        }
    };

    static async updateSubFolder (data: { folder_name: string, spaceFolderId: number, spaceId: number }) {
        try {
            await baseApi.put(`${SUPERADMIN_FOLDERS_API}`, data);
        } catch (error) {
            throw new Error("Failed to add subfolder.");
        }
    };

    static async removeSubFolder (folderId: number, spaceId?: string) {
        try {
            await baseApi.delete(`${SUPERADMIN_FOLDERS_API}?spaceId=${spaceId}&spaceFolderId=${folderId}`);
        } catch (error) {
            throw new Error("Failed to delete folder and its files.");
        }
    };

    //file
    static async updateFileName (id: string, newName: string) {
        try {
            const response = await baseApi.put(`${SUPERADMIN_FILES_API}/${id}/update-name`, { document_name: newName });
            return response.data;
        } catch (error) {
            throw new Error("Failed to update document (super admin).");
        }
    };

    static async deleteFile (id: string) {
        try {
            await baseApi.delete(`${SUPERADMIN_FILES_API}/${id}`);
            return { message: "Document deleted successfully" };
        } catch (error) {
            throw new Error("Failed to delete document (super admin).");
        }
    };

    static async downloadRepoZipFolder (spaceId: string) {
        try {
            const response = await baseApi.get(`${SUPERADMIN_FOLDERS_API}/contents/download?spaceId=${spaceId}`, {
                responseType: 'blob' // Ensure we get the file as a binary blob
            });
    
            // Create a downloadable link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
    
            // Set file name (You can modify this as needed)
            link.setAttribute('download', `space_${spaceId}.zip`);
            document.body.appendChild(link);
            link.click();
    
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download ZIP file:", error);
            throw new Error("Failed to download ZIP file.");
        }
    };

    static async downloadSingleFile (documentId: string, documentName: string) {
        try {
            const response = await baseApi.get(`${SUPERADMIN_FOLDERS_API}/download-file?document_id=${documentId}`, {
                responseType: 'blob' // Ensures we receive the file as binary data
            });
    
            // Extract the file name from Content-Disposition header (if provided)
            const contentDisposition = response.headers['content-disposition'];
            let fileName = `${documentName}`; // Default fallback name
    
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.+)"?/);
                if (match && match[1]) {
                    fileName = match[1];
                }
            }
    
            // Create a downloadable link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName); // Set proper file name
            document.body.appendChild(link);
            link.click();
    
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download file:", error);
            throw new Error("Failed to download file.");
        }
    };
    
    static async downloadSingleFolder (folder: any) {
        try {
            const response = await baseApi.get(`${SUPERADMIN_FOLDERS_API}/download-folder?folder_id=${folder.id}`, {
                responseType: 'blob' // Ensure we get the file as binary data
            });
    
            // Extract the file name from Content-Disposition header (if provided)
            const contentDisposition = response.headers['content-disposition'];
            let fileName = `${folder.folder_name}.zip`; // Default fallback name
    
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.+)"?/);
                if (match && match[1]) {
                    fileName = match[1];
                }
            }
    
            // Create a downloadable link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName); // Set proper file name
            document.body.appendChild(link);
            link.click();
    
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download folder:", error);
            throw new Error("Failed to download folder.");
        }
    };
}

export default SuperAdminFolderService;
